import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhone, FaMapMarkerAlt, FaEnvelope, FaFacebookF, FaWhatsapp, FaInstagram, FaTiktok } from 'react-icons/fa';
import logo from '../assets/Navbar/image.png';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const changeLanguage = (lng) => {
        console.log('Changing language to:', lng);
        i18n.changeLanguage(lng).catch((error) => {
            console.error('Failed to change language:', error);
        });
    };

    const navItems = [
        { to: '/', label: t('home') },
        { to: '/about-us', label: t('about') },
        { to: '/srilanka', label: t('srilanka') },
        { to: '/excursions', label: t('excursions') },
        { to: '/tour', label: t('tour') },
        { to: '/things', label: t('things') },
        { to: '/contact-us', label: t('contact.navLabel') },
    ];

    return (
        <div className="relative">
            {/* Top Section */}
            <div
                className={`flex items-center justify-between text-white ${isScrolled ? 'shadow-sm' : ''
                    } bg-white border-b-4 border-gray-500 px-4 sm:px-6 lg:px-8 relative h-40 sm:h-48 md:h-56 transition-all duration-300`}
            >
                {/* Centered Logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <img
                        src={logo}
                        alt="Cinnamon Mantra"
                        className="h-32 sm:h-64 md:h-64 lg:h-56 w-auto object-contain"
                    />
                </div>

                {/* Language Selector and Social Icons (Desktop) */}
                <div className="hidden md:flex flex-col gap-3 absolute top-4 sm:top-6 md:top-8 lg:top-10 right-4 sm:right-6 lg:ml-10">
                    {/* Language Dropdown */}
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="lg:ml-32 bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base w-32 sm:w-36 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043]"
                        aria-label="Select language"
                    >
                        <option value="en">English</option>
                        <option value="de">German</option>
                        <option value="ru">Russian</option>
                        <option value="fr">French</option>
                        <option value="nl">Dutch</option>
                        <option value="ja">Japanese</option>
                        <option value="zh">Chinese</option>
                        <option value="cs">Czech</option>
                    </select>

                    {/* Social Icons */}
                    <div className="flex gap-3 sm:gap-4 sm:mt-8 md:mt-20">
                        <a href="tel:+94779590011" aria-label="Call us">
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/JxBgkALxB2UmH1369"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                        >
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a href="mailto:ashantourssl@gmail.com" aria-label="Email us">
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://web.facebook.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Facebook page"
                        >
                            <FaFacebookF className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://wa.me/+94779590011"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contact us on WhatsApp"
                        >
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.instagram.com/ashan.tours?igsh=ZW9qZDdmdnNrc3Z1&utm_source=qr"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Instagram page"
                        >
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.tiktok.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our TikTok page"
                        >
                            <FaTiktok className="w-5 h-5 sm:w-6 sm:h-6 text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110" />
                        </a>
                    </div>
                </div>

                {/* Language Selector and Menu Button (Mobile) */}
                <div className="md:hidden flex flex-col gap-3 absolute top-4 right-4">
                    {/* Language Dropdown */}
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg px-3 py-1.5 text-sm w-28 sm:w-32 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043]"
                        aria-label="Select language"
                    >
                        <option value="en">English</option>
                        <option value="de">German</option>
                        <option value="ru">Russian</option>
                        <option value="fr">French</option>
                        <option value="nl">Dutch</option>
                        <option value="ja">Japanese</option>
                        <option value="zh">Chinese</option>
                        <option value="cs">Czech</option>
                    </select>

                    {/* Menu Button */}
                    <button
                        className="text-[#2E4B2C] bg-transparent border-none mt-10 ml-20"
                        onClick={toggleMenu}
                        aria-label="Toggle mobile menu"
                    >
                        <svg
                            className="w-7 h-7 sm:w-8 sm:h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16m-7 6h7"
                            ></path>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Bottom Section (Desktop Navigation) */}
            <div
                className="hidden md:flex justify-center items-center px-4 sm:px-6 lg:px-8 py-2 bg-[#122C56] sticky top-0 z-50"
                style={{ boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none' }}
            >
                <ul className="flex flex-wrap justify-center gap-2 sm:gap-4 lg:gap-6 mb-0 font-serif text-lg sm:text-xl lg:text-2xl">
                    {navItems.map((item) => (
                        <li key={item.to} className="nav-item">
                            <NavLink
                                to={item.to}
                                className={({ isActive }) =>
                                    `nav-link px-3 py-2 sm:px-4 sm:py-2 text-[#FFFFFF] hover:text-[#C58E2F] transition-colors rounded-md ${isActive ? 'underline bg-[#C58E2F] text-[#FFFFFF]' : ''
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="md:hidden fixed top-0 right-0 h-full w-3/4 sm:w-2/3 max-w-xs bg-[#122C56] shadow-2xl border-l border-gray-200 z-50 p-4 sm:p-5 rounded-l-lg animate-slideIn"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                >
                    <style>
                        {`
              @keyframes slideIn {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
                    </style>

                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 bg-[#C58E2F] text-[#FFFFFF] border border-yellow-800 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl sm:text-2xl font-bold transition-all hover:bg-yellow-200 hover:scale-110"
                        onClick={toggleMenu}
                        aria-label="Close mobile menu"
                    >
                        X
                    </button>

                    {/* Navigation Links */}
                    <ul className="flex flex-col mt-16 gap-2">
                        {navItems.map((item) => (
                            <li key={item.to} className="nav-item">
                                <NavLink
                                    to={item.to}
                                    onClick={toggleMenu}
                                    className={({ isActive }) =>
                                        `nav-link text-[#FFFFFF] hover:bg-yellow-100 hover:text-[#C58E2F] transition-all font-serif text-lg sm:text-xl py-2 px-4 block rounded-md ${isActive ? 'underline bg-[#C58E2F]' : ''
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Social Icons */}
                    <div className="flex gap-3 sm:gap-4 mt-4 justify-center border-t border-gray-200 pt-4">
                        <a
                            href="tel:+94779590011"
                            aria-label="Call us"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/JxBgkALxB2UmH1369"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View location"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="mailto:ashantourssl@gmail.com"
                            aria-label="Email us"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="https://web.facebook.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Facebook page"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaFacebookF className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="https://wa.me/+94779590011"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contact us on WhatsApp"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="https://www.instagram.com/ashan.tours?igsh=ZW9qZDdmdnNrc3Z1&utm_source=qr"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our Instagram page"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a
                            href="https://www.tiktok.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visit our TikTok page"
                            className="text-[#C58E2F] hover:text-[#C58E2F] transition-transform transform hover:scale-110"
                        >
                            <FaTiktok className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;